import { NoData, Stack } from '@cast/design-system';

import { useEnterprisePlatformUsageReportQuery } from 'hooks/queries/enterprise/useEnterprisePlatformUsageReportQuery';
import { useNavigate } from '@cast/infrastructure-organization';

import { OverviewCard, OverviewCardSkeleton } from './_components/OverviewCard';
import { SubscriptionsBar } from './_components/SubscriptionsBar';
import { getUnitLabel, sortPlatformUsageFeatures } from './_utils';

import { FailedToLoad } from '@cast/shared-messages-ui';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

export const EnterprisePlatformUsage = () => {
  const navigate = useNavigate();
  const { enterpriseOrgId } = useOrganizations();
  const { data, isLoading, isError } = useEnterprisePlatformUsageReportQuery({
    enterpriseId: enterpriseOrgId ?? '',
  });

  const subscriptions = data?.subscriptions ?? [];
  const features = sortPlatformUsageFeatures(data?.features ?? []);
  if (isError) {
    return <FailedToLoad />;
  }

  const validFeatures = features.filter(({ feature }) => !!feature);

  return (
    <Stack gap={16}>
      <SubscriptionsBar subscriptions={subscriptions} isLoading={isLoading} />
      <Stack gap={24}>
        {isLoading ? (
          Array.from({ length: 3 }, (_, i) => <OverviewCardSkeleton key={i} />)
        ) : validFeatures.length === 0 ? (
          <NoData sx={{ mt: 64 }} entity="feature" />
        ) : (
          validFeatures.map((feature) => {
            const freeOfCharge = feature.freeOfCharge;
            const credits = feature.credits;

            return (
              <OverviewCard
                key={feature.feature}
                testId="platform-usage-overview-card"
                title={feature.displayName ?? feature.feature ?? ''}
                badge={
                  freeOfCharge?.isFreeOfCharge
                    ? {
                        label: 'Free of charge',
                        endDate: freeOfCharge.endDate,
                      }
                    : undefined
                }
                usage={{
                  value: feature.usage ?? 0,
                  unit: getUnitLabel(feature.unit),
                }}
                credits={{
                  used: credits?.used ?? 0,
                  total: credits?.total ?? 0,
                  remaining: credits?.remaining ?? 0,
                  period: credits?.resetFrequency,
                  periodTooltip:
                    credits?.resetFrequency === 'monthly'
                      ? 'Your credit allocation renews on the 1st of each month.'
                      : undefined,
                }}
                onClick={() => navigate(feature.feature!)}
              />
            );
          })
        )}
      </Stack>
    </Stack>
  );
};
